import { useState, useMemo } from "react";
import { useProjectTeam } from "@/hooks/useProjectTeam";
import { useCompanyUsers } from "@/hooks/useCompanyUsers";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProjectTeamPanel = ({ projectId, open, onOpenChange }: Props) => {
  const { members, isLoading, addMember, removeMember } = useProjectTeam(projectId);
  const { data: companyUsers } = useCompanyUsers();
  const { can, isAdmin } = usePermissions();
  const canManage = isAdmin || can("edit_project_info");
  const [search, setSearch] = useState("");

  const assignedIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);

  const filteredCandidates = useMemo(() => {
    if (!companyUsers || !search.trim()) return [];
    const q = search.toLowerCase();
    return companyUsers
      .filter((u) => !assignedIds.has(u.user_id))
      .filter((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [companyUsers, assignedIds, search]);

  const handleAdd = async (userId: string) => {
    try {
      await addMember.mutateAsync(userId);
      toast.success("Team member added");
      setSearch("");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to add member");
    }
  };

  const handleRemove = async (assignmentId: string, name: string) => {
    try {
      await removeMember.mutateAsync(assignmentId);
      toast.success(`${name} removed from project`);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to remove member");
    }
  };

  const initials = (first: string, last: string) =>
    `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Project Team</SheetTitle>
          <SheetDescription>Manage team members assigned to this project.</SheetDescription>
        </SheetHeader>

        {/* Add Member */}
        {canManage && (
          <div className="mt-4 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users to add…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {filteredCandidates.length > 0 && (
              <div className="border rounded-md max-h-48 overflow-auto">
                {filteredCandidates.map((u) => (
                  <button
                    key={u.user_id}
                    className="flex items-center gap-3 w-full px-3 py-2 hover:bg-muted/50 text-left text-sm transition-colors"
                    onClick={() => handleAdd(u.user_id)}
                    disabled={addMember.isPending}
                  >
                    <Avatar className="h-7 w-7">
                      {u.profile_photo_url && <AvatarImage src={u.profile_photo_url} />}
                      <AvatarFallback className="text-[10px]">
                        {initials(u.first_name, u.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{u.display_name || `${u.first_name} ${u.last_name}`}</p>
                      <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Current Team */}
        <ScrollArea className="flex-1 mt-4 -mx-6 px-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No team members assigned yet.</p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {members.length} member{members.length !== 1 ? "s" : ""}
              </p>
              {members.map((m) => (
                <div key={m.assignment_id} className="flex items-center gap-3 py-2 px-1 rounded-md hover:bg-muted/30 group">
                  <Avatar className="h-8 w-8">
                    {m.profile_photo_url && <AvatarImage src={m.profile_photo_url} />}
                    <AvatarFallback className="text-xs">
                      {initials(m.first_name, m.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {m.display_name || `${m.first_name} ${m.last_name}`}
                      </p>
                      {m.role_name && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 shrink-0"
                          style={m.role_color ? { borderColor: m.role_color, color: m.role_color } : undefined}
                        >
                          {m.role_name}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                  </div>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={() => handleRemove(m.assignment_id, m.display_name || m.first_name)}
                      disabled={removeMember.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
